<app-pan-card
  header="Market Snapshots"
  [subheader]="pageSubheader()"
  [isPageContainer]="true"
  pageContainerSize="xl"
  data-testid="market-snapshots-page"
>
  <div rightActions class="flex gap-2">
    <app-pan-button
      color="info"
      icon="folder_zip"
      (click)="exportAvailableImages()"
      [disabled]="totalImageCount() === 0"
      data-testid="market-snapshots-export-button"
      >Export {{ totalImageCount() }} Image(s)</app-pan-button
    >

    <app-pan-help size="md" helpText="Snapshots shown are from cameras at the scheduled time for each location."></app-pan-help>
  </div>

  <app-page-filters
    [allowBranchSelection]="true"
    [branchRequestParams]="{ market_snapshots_enabled: true }"
    [branchSelectionAutoSelectBranchIds]="pageBranchId() !== null ? [pageBranchId()!] : []"
    [(selectedBranches)]="selectedBranch"
    (selectedBranchesChange)="onBranchChange()"
    [allowLocationSelection]="selectedBranch() && locationOptions().length > 0"
    [locationSelectionAutoSelectLocationIds]="pageLocationIds()"
    [(selectedLocations)]="selectedLocations"
    (selectedLocationsChange)="onLocationsChange()"
    locationSelectionLabel="Select Locations"
    [locationSelectionMulti]="true"
    [locationShowBranchName]="false"
    [locationOverrideLocations]="locationOptions()"
    [locationForceShowOverrideLocationsEvenIfEmpty]="true"
  >
  </app-page-filters>

  <div class="grid md:grid-cols-8 sm:grid-cols-1 gap-2">
    <div class="col-span-1 flex gap-2 items-baseline">
      <app-pan-form-control>
        <app-pan-floating-label>Date</app-pan-floating-label>
        <app-pan-datepicker
          [(date)]="snapshotDate"
          (dateChange)="loadMarketSnapshots()"
          placeholder="Select Date"
          data-testid="market-snapshots-date-input"
        />
      </app-pan-form-control>
    </div>
    <div class="col-span-1">
      <app-pan-form-control>
        <app-pan-floating-label>Status</app-pan-floating-label>
        <ng-select
          appendTo="body"
          [items]="snapshotStatusOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [hideSelected]="false"
          placeholder="Select status..."
          [ngModel]="selectedSnapshotStatuses()"
          (ngModelChange)="selectedSnapshotStatuses.set($event ?? [])"
          clearAllText="Clear"
        >
        </ng-select>
      </app-pan-form-control>
    </div>
  </div>

  @if (isLoadingSnapshots()) {
    <app-pan-blank-slate data-testid="market-snapshots-loading"
      ><app-pan-spinner></app-pan-spinner> Loading snapshots...</app-pan-blank-slate
    >
  } @else if (emptyMessage(); as message) {
    <app-pan-blank-slate>{{ message }}</app-pan-blank-slate>
  } @else {
    <div class="mt-2 grid sm:grid-cols-1 lg:grid-cols-3 gap-2">
      @for (snapshot of filteredSnapshots(); track snapshot.locationId) {
        @let imageRef = previewImage$(snapshot) | async;
        @let isImageLoading = imageRef === null || imageRef?.isLoading;
        @let hasNoCamera = snapshot.cameraCount === 0;
        @let previewError = snapshot.images[0]?.snapshotError;

        <app-pan-card data-testid="market-snapshot-card">
          <div class="flex flex-col h-[300px] rounded-lg overflow-hidden">
            <div class="h-[210px] flex items-center justify-center relative">
              @if (hasNoCamera) {
                <app-pan-blank-slate data-testid="market-snapshot-no-camera">
                  <mat-icon class="scale-150">videocam_off</mat-icon>
                  <span class="text-sm">No camera is connected for this location</span>
                </app-pan-blank-slate>
              } @else if (previewError) {
                <app-pan-blank-slate data-testid="market-snapshot-camera-error">
                  <mat-icon class="scale-150">videocam_off</mat-icon>
                  <span class="text-sm">{{ cameraErrorMessage(snapshot.images[0]) }}</span>
                </app-pan-blank-slate>
              } @else if (isImageLoading) {
                <app-pan-blank-slate>
                  <app-pan-spinner size="xl"></app-pan-spinner>
                </app-pan-blank-slate>
              } @else if (imageRef?.value) {
                <img [alt]="snapshot.locationName" class="h-full w-full object-cover" [src]="imageRef.value | blobToImage" />
                @if (snapshot.status !== "Unassigned") {
                  <div class="absolute top-0 right-0 px-2 py-1 rounded-b-lg rounded-br-none bg-black/30">
                    <div class="text-white text-xs font-medium flex items-center gap-2">
                      <span
                        class="inline-block rounded-lg w-2.5 h-2.5"
                        [class.bg-success-no-hover]="snapshot.status === 'Healthy'"
                        [class.bg-warning-no-hover]="snapshot.status === 'Warning'"
                        [class.bg-danger-no-hover]="snapshot.status === 'Unhealthy'"
                      ></span>
                      <span>{{ snapshot.status }}</span>
                    </div>
                  </div>
                }
              } @else {
                <app-pan-blank-slate>
                  <mat-icon>videocam_off</mat-icon>
                </app-pan-blank-slate>
              }
            </div>

            <div class="flex items-center justify-between gap-3 p-3 text-left border-t border-slate-200 bg-white">
              <div class="min-w-0">
                <div class="font-semibold text-lg truncate">
                  {{ snapshot.locationName }}
                </div>

                <div class="text-sm text-slate-500">
                  {{ snapshot.images[0]?.requestDateTime ? (snapshot.images[0].requestDateTime | date: "MM/dd/yyyy, hh:mm a") : "" }}
                </div>
              </div>

              <div class="shrink-0">
                @if (hasNoCamera) {
                  <app-pan-button
                    size="md"
                    color="info"
                    (click)="goToInstallerDashboard(snapshot)"
                    data-testid="market-snapshot-installer-dashboard-button"
                    panRole
                    [roles]="['admin', 'branch-owner', 'branch-manager', 'installer', 'self-service']"
                  >
                    Installer Dashboard
                  </app-pan-button>
                } @else {
                  <app-pan-button size="md" color="info" (click)="viewSnapshot(snapshot)" data-testid="market-snapshot-view-button">
                    {{ snapshot.cameraCount > 1 ? "View (" + snapshot.cameraCount + ") Cameras" : "View Camera" }}
                  </app-pan-button>
                }
              </div>
            </div>
          </div>
        </app-pan-card>
      }
    </div>
  }
</app-pan-card>
